import React from 'react';
import { Mail, Phone, MapPin, Github, Linkedin, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { portfolioData } from '../mock';
import { submitContactForm } from '../firebase';
import { useToast } from '../hooks/use-toast';

const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  subject: z.string().min(5, "Subject must be at least 5 characters"),
  message: z.string().min(10, "Message must be at least 10 characters")
});

const Contact = () => {
  const { personal } = portfolioData;
  const { toast } = useToast();
  const form = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: ""
    }
  });

  const onSubmit = async (data) => {
    try {
      // Save to Firebase Firestore
      await submitContactForm(data);

      // Send emails via Vercel serverless function
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error("Email send failed");

      toast({
        title: "Message sent successfully!",
        description: "Thank you for reaching out. I'll get back to you soon.",
      });
      form.reset();
    } catch {
      toast({
        title: "Failed to send message",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-slate-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Get In Touch
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">Let's discuss your next project</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-slate-900/50 border-slate-700 hover:border-cyan-400/50 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="bg-cyan-400/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-400/20 transition-colors">
                  <Mail className="text-cyan-400" size={28} />
                </div>
                <h4 className="text-white font-semibold mb-2">Email</h4>
                <a
                  href={`mailto:${personal.email}`}
                  className="text-gray-400 hover:text-cyan-400 transition-colors text-sm break-all"
                >
                  {personal.email}
                </a>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-700 hover:border-cyan-400/50 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="bg-cyan-400/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-400/20 transition-colors">
                  <Phone className="text-cyan-400" size={28} />
                </div>
                <h4 className="text-white font-semibold mb-2">Phone</h4>
                <a
                  href={`tel:${personal.phone}`}
                  className="text-gray-400 hover:text-cyan-400 transition-colors text-sm"
                >
                  {personal.phone}
                </a>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-700 hover:border-cyan-400/50 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="bg-cyan-400/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-400/20 transition-colors">
                  <MapPin className="text-cyan-400" size={28} />
                </div>
                <h4 className="text-white font-semibold mb-2">Location</h4>
                <p className="text-gray-400 text-sm">{personal.location}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-slate-900/50 border-cyan-400/30 mb-12 shadow-lg shadow-cyan-400/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl font-bold text-white text-center">
                Send a Message
              </CardTitle>
              <p className="text-gray-400 text-sm text-center">I'll get back to you within 24–48 hours</p>
            </CardHeader>
            <CardContent className="pt-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300 text-sm font-medium">Your Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Your Name"
                              className="bg-slate-800 border-slate-600 text-white placeholder:text-gray-500 focus:border-cyan-400 focus-visible:ring-cyan-400/20 transition-colors"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300 text-sm font-medium">Your Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="you@example.com"
                              className="bg-slate-800 border-slate-600 text-white placeholder:text-gray-500 focus:border-cyan-400 focus-visible:ring-cyan-400/20 transition-colors h-10 leading-normal"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300 text-sm font-medium">Subject</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="What's this about?"
                            className="bg-slate-800 border-slate-600 text-white placeholder:text-gray-500 focus:border-cyan-400 focus-visible:ring-cyan-400/20 transition-colors"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300 text-sm font-medium">Message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tell me about your project or query..."
                            className="bg-slate-800 border-slate-600 text-white placeholder:text-gray-500 min-h-[140px] focus:border-cyan-400 focus-visible:ring-cyan-400/20 transition-colors resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-6 text-base font-semibold transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-cyan-500/20"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} className="mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;